import React, {useRef} from "react";
import {Icon} from "@paytheory/components.common.icon";
import {useButton, AriaButtonProps} from "react-aria";

export interface ButtonProps extends AriaButtonProps {
    primary?: boolean;
    text?: boolean;
    blackText?: boolean;
    leadingIcon?: {
        name: string;
        brand?: boolean;
    };
    trailingIcon?: {
        name: string;
        brand?: boolean;
    };
    thin?: boolean;
    id?: string;
}

export const Button = (props: ButtonProps) => {
    const {
        primary,
        text,
        blackText,
        leadingIcon,
        trailingIcon,
        children,
        isDisabled,
        id,
        thin
    } = props;
    const button = useRef(null);
    let {buttonProps} = useButton(props, button);

    const styleClass = primary ? "primary" : text ? "text" : blackText ? "black text" : "";
    return (
        <button
            className={`pt-button ${styleClass} ${thin ? "thin" : ""} ${
                isDisabled ? "disabled" : ""
            }`}
            data-testid={id}
            ref={button}
            {...buttonProps}
        >
      <span className="content">
        {leadingIcon ? (
            <Icon
                name={leadingIcon.name}
                brand={leadingIcon.brand}
                label="leading"
            />
        ) : null}
          <span className="text">{children}</span>
          {trailingIcon ? (
              <Icon
                  name={trailingIcon.name}
                  brand={trailingIcon.brand}
                  label="trailing"
              />
          ) : null}
      </span>
            {/*@ts-ignore*/}
            <style jsx="true">
                {`
                  .pt-button {
                    position: relative;
                    align-self: flex-start;
                    outline: 0;
                    height: 48px;
                    min-height: 48px;
                    padding: 0px 16px;
                    border-radius: 12px;
                    border: 0px solid transparent;
                    cursor: pointer;
                    font-family: var(--secondary-font);
                    text-transform: uppercase;
                    font-size: 16px;
                    color: var(--black);
                    background-color: var(--grey-1-opaque-32);
                    transition: background 0.15s ease-in-out, border 0.15s ease-in-out,
                    padding 0.15s ease-in-out;
                  }

                  .pt-button.thin:not(.text) {
                    height: 40px;
                    min-height: 40px;
                  }

                  .pt-button .content {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    height: 18px;
                  }

                  .pt-button .content .text {
                    padding-bottom: 4px;
                  }

                  .pt-button:hover {
                    background-color: var(--grey-1-opaque);
                    transition: background 0.15s ease-in-out, padding 0.15s ease-in-out;
                  }

                  .pt-button:not(.text):focus-visible {
                    outline: 4px solid var(--pt-purple);
                    transition: border 0.15s ease-in-out;
                  }

                  .pt-button .pt-icon.leading {
                    margin-right: 8px;
                  }

                  .pt-button .pt-icon.trailing {
                    margin-left: 8px;
                  }

                  /*Disabled Styling*/

                  .pt-button.disabled {
                    cursor: default;
                    color: var(--grey-1);
                    background-color: var(--grey-2);
                    box-shadow: none;
                    background-image: none;
                  }

                  /*Primary Button Styling*/

                  .pt-button.primary:not(.disabled) {
                    background: var(--pt-purple);
                    color: var(--white);
                  }

                  .pt-button.primary:not(.disabled):hover {
                    background: var(--pt-purple-opaque-56);
                  }

                  .pt-button.cta:focus-visible,
                  .pt-button.primary:focus-visible {
                    outline: 4px solid var(--pink);
                    transition: all 0.15s ease-in-out;
                  }

                  /*Text Button Styling*/

                  .pt-button.text:not(.disabled) {
                    height: auto;
                    padding: 0px;
                    background: transparent;
                    color: var(--pt-purple);
                    transition: border 0.5s ease;
                  }
                  
                  .pt-button.black.text:not(.disabled) {
                    color: var(--black);
                  }

                  .pt-button.text:after {
                    content: "";
                    display: block;
                    margin: auto;
                    padding-top: 2px;
                    height: 1px;
                    width: 0px;
                    border-bottom: 1px solid transparent;
                    transition: width 0.5s ease, border 0.5s ease;
                  }

                  .pt-button.text:not(.disabled):hover:after,
                  .pt-button.text:not(.disabled):focus-visible:after {
                    width: 100%;
                    border-bottom: 1px solid var(--pt-purple);
                  }
                  
                  .pt-button.black.text:not(.disabled):hover:after,
                  .pt-button.black.text:not(.disabled):focus-visible:after {
                    width: 100%;
                    border-bottom: 1px solid var(--black);
                  }

                  .pt-button.text.disabled {
                    cursor: default;
                    color: var(--grey-1);
                    box-shadow: none;
                    height: auto;
                    padding: 0px;
                    background: transparent;
                  }
                `}
            </style>
        </button>
    );
};